import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import html2canvas from "html2canvas";
import logo from "../images/safety-hero-logo.png";
import stamp from "../images/certificate-stamp.png";
import { useGameStore } from "../store/gameStore";

const Certificate = () => {
  const navigate = useNavigate();
  const { userName, getFormattedPlayTime, isGameCompleted, getFinalPlayTime } =
    useGameStore();
  const [certificateNumber, setCertificateNumber] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const certificateRef = useRef(null);

  // 인증번호 생성 (0000~9999)
  useEffect(() => {
    const randomNumber = Math.floor(Math.random() * 10000);
    const formattedNumber = randomNumber.toString().padStart(4, "0");
    setCertificateNumber(`HERO-${formattedNumber}`);
  }, []);

  // 인증서 이미지 생성 및 다운로드
  const generateAndDownloadCertificate = async () => {
    if (!certificateRef.current || isGenerating) return;

    setIsGenerating(true);
    try {
      const canvas = await html2canvas(certificateRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
      });

      const image = canvas.toDataURL("image/png");

      const link = document.createElement("a");
      link.download = `안전히어로_인증서_${userName}_${certificateNumber}.png`;
      link.href = image;
      link.click();

      alert("인증서 이미지가 다운로드되었습니다!");
    } catch (error) {
      console.error("인증서 생성 실패:", error);
      alert("인증서 생성에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setIsGenerating(false);
    }
  };
  return (
    <div className="min-h-screen flex flex-col px-4 py-4">
      <div className="flex flex-col bg-white/40 rounded-2xl px-4 py-2">
        <header className="flex flex-col items-center justify-center">
          <div className="pl-5 w-screen h-28 flex items-center justify-center mb-4">
            <img
              src={logo}
              alt="Safety Hero Logo"
              className="w-full h-full object-contain"
            />
          </div>
        </header>

        <div className="flex items-center justify-center mb-2">
          <div
            ref={certificateRef}
            className="w-full max-w-md bg-white rounded-2xl border-4 border-red-500 px-6 py-4 shadow-2xl relative"
          >
            <div className="absolute top-1 -right-2 w-28 h-28">
              <img
                src={stamp}
                alt="Safety Hero Stamp"
                className="w-full h-full object-contain drop-shadow-lg"
              />
            </div>

            <div className="text-center mb-4">
              <p className="text-xl font-bold text-black">이름: {userName}</p>
            </div>

            <div className="text-center mb-6">
              <p className="text-xl font-bold text-black">
                기록:{" "}
                {isGameCompleted ? getFinalPlayTime() : getFormattedPlayTime()}
              </p>
            </div>

            <div className="text-center space-y-1 mb-2">
              <p className="text-[15px] leading-relaxed text-gray-800">
                위 어린이는 안전히어로 탐험대의
                <br />
                모든 미션을 훌륭히 수행하고
                <br />
                안전 상식 퀴즈를 모두 맞혀
                <br />
                안전을 위협하는 몬스터들을 물리쳤습니다.
                <br />
                <br />
                뛰어난 관찰력과 용기를 보여준 당신은
                <br />
                자랑스러운 안전히어로임을 인증합니다.
                <br />
                <br />
                앞으로 안전히어로로써
                <br />
                세상을 안전하게 만들어주세요!
              </p>
            </div>

            <div className="space-y-2 mb-2">
              <p className="text-sm text-gray-700">
                날짜: {new Date().toLocaleDateString()}
              </p>
              <p className="text-sm text-gray-700">
                인증번호: {certificateNumber}
              </p>
            </div>

            <div className="text-center pt-2">
              <p className="text-sm text-gray-700 font-semibold whitespace-pre-line">
                {`발급기관: 대교 마이다스 호텔&리조트 
                안전히어로 탐험대`}
              </p>
            </div>
          </div>
        </div>

        <section className="flex space-x-4 pb-2">
          <button
            className="flex-1 px-4 py-2 bg-yellow-400 hover:bg-yellow-500 text-gray-800 text-lg font-bold rounded-xl shadow-lg transform transition-all duration-300 hover:scale-105 active:scale-95"
            onClick={() => setShowShareModal(true)}
          >
            친구들에게
            <br />
            자랑하기
          </button>

          <button
            className="flex-1 px-4 py-2 bg-yellow-400 hover:bg-yellow-500 text-gray-800 text-lg font-bold rounded-xl shadow-lg transform transition-all duration-300 hover:scale-105 active:scale-95"
            onClick={() => {
              navigate("/");
            }}
          >
            종료하기
          </button>
        </section>
      </div>

      {/* 공유 모달 */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                친구들에게 자랑하기
              </h3>
              <p className="text-gray-600">
                인증서를 공유하거나 이미지를 다운로드하세요
              </p>
            </div>

            <div className="space-y-4">
              <button
                onClick={generateAndDownloadCertificate}
                disabled={isGenerating}
                className="w-full p-4 bg-green-500 hover:bg-green-600 text-white font-bold rounded-xl shadow-lg transform transition-all duration-300 hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGenerating
                  ? "🔄 이미지 생성 중..."
                  : "📸 인증서 이미지 다운로드"}
              </button>
            </div>

            <button
              onClick={() => setShowShareModal(false)}
              className="w-full mt-6 p-3 bg-gray-300 hover:bg-gray-400 text-gray-700 font-bold rounded-xl transition-all duration-300"
            >
              닫기
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Certificate;
